import React from 'react'
import classnames from 'classnames/bind'
import _ from 'lodash'
// @ts-ignore no types
import { Text, LabelValue, InfoPill } from '../../../../elementals'
import { PropsOf } from '../../../../../types'
import styles from './styles.scss'
import type { CurrentTargetAudience, Listing } from '@adc/sdk/types'
import ListingSellerRepresentativeCard from '../../../../listingSellerRepresentativeCard'
import Icon from '../../../../elementals/icon'
import { InfoPillProps } from '../../../../elementals/infoPill'
import PopupTooltip from '../../../../overlays/popupTooltip'

const cx = classnames.bind(styles)

type SubsectionChild = PropsOf<typeof LabelValue> & {
  width: string
  type: 'LABEL' | 'LINE_SEPARATOR' | 'LISTING_AGENT' | 'INFO_PILL'
  className?: string
  hide?: boolean
  infoPill?: InfoPillProps
  viewingFrom?: CurrentTargetAudience
}

type Subsection = {
  title?: string
  children: SubsectionChild[]
  className?: string
  tooltip?: string
}

type SummarySectionProps = {
  title?: string
  subsections: Subsection[]
  listing?: Listing
  className?: string
}

const SummarySection = ({
  subsections,
  listing,
  className,
}: SummarySectionProps) => {
  const displayChildren = (key: string, children: SubsectionChild[]) => {
    return children.map((c, i) => {
      switch (c.type) {
        case 'LINE_SEPARATOR':
          return <hr key={`${key}-${i}`} className={c.className} />
        case 'LISTING_AGENT':
          return (
            <ListingSellerRepresentativeCard
              key={`${key}-${i}`}
              listingParties={listing?.parties || []}
              className={c.className}
              open
              viewingFrom={c.viewingFrom}
            />
          )
        case 'INFO_PILL':
          return (
            <InfoPill
              size={c.infoPill?.size}
              backgroundColor={c.infoPill?.backgroundColor}
              foregroundColor={c.infoPill?.foregroundColor}
              includeBorder={c.infoPill?.includeBorder}
              iconName={c.infoPill?.iconName}
              extraPadding={c.infoPill?.extraPadding}
              content={c.infoPill?.content}
              fontWeight={c.infoPill?.fontWeight}
              elmId={c.infoPill?.elmId}
            />
          )
        default:
          return (
            <div
              key={`${key}-${i}`}
              style={{ flex: `1 1 calc(50% - 18px)`, wordBreak: 'break-word' }}
            >
              <LabelValue {..._.omit(c, 'width', 'type')} />
            </div>
          )
      }
    })
  }
  return (
    <div className={cx('section-container', className)}>
      {subsections.map((s, index) => (
        <div
          key={s.title || `title_${index}`}
          data-elm-id={`${
            s.title?.toLowerCase().replace(/\s/g, '-') || ''
          }-subsection`}
          className={cx('u-mb-2')}
        >
          <Text
            className={cx('u-mb-2')}
            color='grayMedium'
            size='large'
            weight='heavy'
          >
            {s.title}
            {s.tooltip && (
              <span className='u-ml-1'>
                <PopupTooltip
                  offset={0}
                  horizontalOffset={0}
                  verticalOffset={0}
                  content={s.tooltip}
                  wide={false}
                  trigger={
                    <Icon
                      size='small'
                      color='gray'
                      name='info-circle'
                      fitted
                      style={{ verticalAlign: 'super' }}
                      iconStyle='solid'
                    />
                  }
                />
              </span>
            )}
          </Text>
          <div className={cx('section-children-container', s.className)}>
            {displayChildren(
              s.title ?? '',
              s.children.filter(c => !c.hide)
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export default SummarySection
